import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SlidersHorizontal, X } from "lucide-react";

const CATEGORY_KEYS = ["all", "femme", "homme", "enfants", "chaussures", "sacs", "accessoires", "vintage"];

export default function Browse() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "all";
  const condition = params.get("condition") || "any";
  const size = params.get("size") || "any";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (category !== "all") p.category = category;
    if (condition !== "any") p.condition = condition;
    if (size !== "any") p.size = size;
    return p;
  }, [q, category, condition, size]);

  useEffect(() => {
    setLoading(true);
    api.get("/listings", { params: query })
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [query]);

  const updateParam = (key, val, defaultVal) => {
    const next = new URLSearchParams(params);
    if (val === defaultVal || !val) next.delete(key);
    else next.set(key, val);
    setParams(next, { replace: true });
  };

  const hasActiveFilters = category !== "all" || condition !== "any" || size !== "any" || q;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <section className="pt-10 pb-6">
        <div className="overline text-primary">{t("browse.kicker")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{t("browse.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("browse.sub")}</p>
      </section>

      <section id="feed" className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-background/85 backdrop-blur border-y border-border/60">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {CATEGORY_KEYS.map((key) => (
            <button
              key={key}
              data-testid={`filter-cat-${key}`}
              onClick={() => updateParam("category", key, "all")}
              className={`shrink-0 px-4 h-9 rounded-full text-sm border transition-colors ${
                category === key
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background text-foreground border-border hover:border-foreground/40"
              }`}
            >
              {t(`browse.cat_${key}`)}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Select value={condition} onValueChange={(v) => updateParam("condition", v, "any")}>
              <SelectTrigger className="h-9 rounded-full min-w-[140px]" data-testid="filter-condition">
                <SelectValue placeholder={t("browse.state_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("browse.state_all")}</SelectItem>
                <SelectItem value="new">{t("condition.new")}</SelectItem>
                <SelectItem value="like_new">{t("condition.like_new")}</SelectItem>
                <SelectItem value="very_good">{t("condition.very_good")}</SelectItem>
                <SelectItem value="good">{t("condition.good")}</SelectItem>
                <SelectItem value="fair">{t("condition.fair")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={size} onValueChange={(v) => updateParam("size", v, "any")}>
              <SelectTrigger className="h-9 rounded-full min-w-[110px]" data-testid="filter-size">
                <SelectValue placeholder={t("browse.size_placeholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("browse.size_all")}</SelectItem>
                {["XS","S","M","L","XL","34","36","37","38","40","42","44","Unique"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <button
                onClick={() => setParams({}, { replace: true })}
                data-testid="filter-clear"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 h-9"
              >
                <X className="w-3.5 h-3.5" /> {t("browse.clear")}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-8">
        {q && (
          <div className="mb-6 text-sm text-muted-foreground">
            <SlidersHorizontal className="w-4 h-4 inline mr-2" />
            {t("browse.results_for")} <span className="text-foreground font-medium">« {q} »</span>
            {" · "} {items.length}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-muted rounded-xl" />
                <div className="h-4 mt-3 bg-muted rounded w-1/2" />
                <div className="h-3 mt-2 bg-muted rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center">
            <div className="text-lg font-display text-foreground">{t("browse.empty_title")}</div>
            <p className="mt-2 text-sm text-muted-foreground">{t("browse.empty_sub")}</p>
            <Link to="/sell" className="inline-block mt-6">
              <Button className="rounded-full">{t("browse.empty_cta")}</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 stagger" data-testid="product-grid">
            {items.map((it) => (
              <ProductCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
